import React from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { Msr, RegSteps, stepsFor, useMaterialSymbols } from "./components";
import { money, summarizeQuote } from "./quoteRows";
import { stripeAppearance } from "../../PaymentRequest/stripeAppearance";
import "./oireachtas.css";
import "./t7.css";

// T7 — the Stripe payment leg for the teacher checkout, laid out per the
// design mockup (7-payment.html): card-details shell around the Stripe
// PaymentElement, the server-quoted order summary aside, and the sticky
// action bar whose Pay button submits the Stripe form. Same PaymentElement +
// confirmPayment pattern as the parent feis flow; the webhook converts the
// checkout to entries and the success page is presentational.

const BASE = process.env.REACT_APP_BASE_URL;
const stripePromise = process.env.REACT_APP_PUBLISHKEY ? loadStripe(process.env.REACT_APP_PUBLISHKEY) : null; // no key on the local site

// Match the PaymentElement's inputs to the design system (shared with the
// payment-request pay page).
const appearance = stripeAppearance;

// `kind`: "full" (default), "firstInstalment" (pays part of the order now,
// card 15862648) or "balance" (a later payment toward an instalment order).
// `amountCents` is what the PaymentIntent charges; the quote is display only.
function PayBody({ eventId, quote, amountCents, kind, totalCents, balanceAfterCents, balanceDueAt }) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const isBalance = kind === "balance";
  const breakdown = isBalance ? null : quote && quote.breakdown;
  const rows = breakdown ? summarizeQuote(breakdown) : [];
  const chargeCents =
    amountCents != null ? amountCents : breakdown && breakdown.totalCents != null ? breakdown.totalCents : null;
  // A first instalment shows the server's frozen plan (total / balance after),
  // never a re-quote of the cart, which may differ from the order charged.
  const partial = kind === "firstInstalment" && balanceAfterCents != null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // The confirmation page shows the balance left from these (the webhook
        // may not have recorded this payment yet when Stripe redirects).
        return_url:
          `${process.env.REACT_APP_BASE}/teacher/oireachtas/${eventId}/register/success?kind=${kind || "full"}` +
          (balanceAfterCents != null ? `&after=${balanceAfterCents}` : "") +
          (balanceDueAt ? `&due=${encodeURIComponent(balanceDueAt)}` : ""),
      },
    });
    if (error) setErrorMessage(error.message);
    setLoading(false);
  };

  return (
    <>
      {errorMessage && <div className="guard-errors">{errorMessage}</div>}
      <div className="grid">
        <div>
          <form id="oir-payform" className="pcard" onSubmit={handleSubmit}>
            <div className="pcard-h">
              <span className="nm">Card details</span>
              <div className="cardbrands">
                <span className="brand visa">VISA</span>
                <span className="brand mc">MC</span>
                <span className="brand amex">AMEX</span>
              </div>
            </div>
            <div className="pform">
              {/* Link is disabled account-side (payment method configuration
                  link.display_preference=off) — there is no per-element
                  switch in this Elements API version. */}
              <PaymentElement />
              <div className="secureline">
                <Msr>lock</Msr>Your payment is encrypted and secure.
                <span className="stripe">Powered by <b>stripe</b></span>
              </div>
            </div>
          </form>
        </div>

        <div className="aside">
          <div className="pay">
            <h2>{isBalance ? "Balance payment" : "Order total"}</h2>
            <div className="pay-body">
              {isBalance && chargeCents != null && (
                <>
                  <div className="prow total">
                    <span className="k">Paying now</span>
                    <span className="v">{money(chargeCents)}</span>
                  </div>
                  {balanceAfterCents != null && (
                    <div className="prow"><span className="k">Balance after this payment</span><span className="v">{money(balanceAfterCents)}</span></div>
                  )}
                </>
              )}
              {!isBalance && !partial && !breakdown && <div className="prow"><span className="k">Loading order…</span></div>}
              {/* The itemised re-quote is shown only when it still matches the
                  order being charged; a first instalment's figures come from
                  the server's frozen plan and never wait on the re-quote. */}
              {breakdown && (!partial || breakdown.totalCents === totalCents) && (
                <>
                  {rows.map((l, i) => (
                    <div className="prow" key={i}>
                      <span className="k">{l.label}{l.sub && <span className="subk">{l.sub}</span>}</span>
                      <span className="v">{money(l.amount)}</span>
                    </div>
                  ))}
                  {breakdown.subtotalCents != null && (
                    <div className="prow subtotal"><span className="k">Subtotal</span><span className="v">{money(breakdown.subtotalCents)}</span></div>
                  )}
                  {breakdown.serviceCents != null && breakdown.serviceCents > 0 && (
                    <div className="prow"><span className="k">Service fee</span><span className="v">{money(breakdown.serviceCents)}</span></div>
                  )}
                  {!partial && (
                    <div className="prow total">
                      <span className="k">Total</span>
                      <span className="v">{money(breakdown.totalCents)}</span>
                    </div>
                  )}
                </>
              )}
              {partial && (
                <>
                  {totalCents != null && (
                    <div className="prow total"><span className="k">Entry total</span><span className="v">{money(totalCents)}</span></div>
                  )}
                  <div className="prow subtotal"><span className="k">Paying now</span><span className="v">{money(chargeCents)}</span></div>
                  <div className="prow"><span className="k">Balance after this payment</span><span className="v">{money(balanceAfterCents)}</span></div>
                </>
              )}
            </div>
            <div className="payer"><Msr>account_balance</Msr>Charged to the school account in one transaction.</div>
            <div className="paynote"><Msr>info</Msr>
              {isBalance
                ? "No refunds. This payment is applied to your school's balance."
                : partial
                  ? "No refunds. Your entries are confirmed once this first payment is received."
                  : "No refunds. Registration completes once payment is received."}
            </div>
          </div>
        </div>
      </div>

      <div className="actionbar">
        <div className="actionbar-in">
          <button type="button" className="ab-back" onClick={() => window.history.back()}>
            <Msr>arrow_back</Msr>Review
          </button>
          <div className="ab-status">
            <span className="sv"><Msr>lock</Msr>Encrypted &amp; secure · Powered by Stripe</span>
          </div>
          <button type="submit" form="oir-payform" className="ab-next" disabled={!stripe || loading}>
            {loading
              ? <><span className="spinner"></span>Processing…</>
              : <><Msr>lock</Msr>Pay{chargeCents != null ? ` ${money(chargeCents)}` : ""}</>}
          </button>
        </div>
      </div>
    </>
  );
}

const PaymentOireachtas = ({ client, eventId, amountCents, kind, totalCents, balanceAfterCents, balanceDueAt }) => {
  const isBalance = kind === "balance";
  useMaterialSymbols();
  const navigate = useNavigate();
  const [quote, setQuote] = React.useState(null);
  const [school, setSchool] = React.useState(null);

  // The shell hands over only the client secret; re-quote the SAVED cart
  // server-side (empty body = price the persisted draft) for the summary, and
  // read the school name off the roster. Presentation only — the charge amount
  // lives in the PaymentIntent the backend already created.
  React.useEffect(() => {
    let alive = true;
    // A balance payment isn't a cart checkout: no draft quote, no stepper.
    if (!isBalance) {
      serviceConsumer("POST", `${BASE}/oireachtas/${eventId}/quote`, {})
        .then((q) => { if (alive) setQuote(q); })
        .catch(() => {});
    }
    serviceConsumer("GET", `${BASE}/oireachtas/${eventId}/roster`)
      .then((r) => { if (alive && r.roster && r.roster.length) setSchool(r.roster[0].schoolName); })
      .catch(() => {});
    return () => { alive = false; };
  }, [eventId, isBalance]);

  return (
    <div className="oir opay">
      <div className="wrap narrow">
        <h1 className="ptitle">{isBalance ? "Balance payment" : "Payment"}</h1>
        {school && <div className="psch"><Msr>school</Msr>{school}</div>}
        {/* This page never loads the event doc — the assessment step's presence
            is read off the quote instead (breakdown.reclaim is non-null iff the
            event has an assessment, the same condition stepsFor checks). */}
        {!isBalance && <RegSteps
          steps={stepsFor({
            fees: {
              schoolAssessmentPerDancer:
                quote && quote.breakdown && quote.breakdown.reclaim ? 1 : 0,
            },
          })}
          current="checkout"
          onGo={(key) => key !== "checkout" && navigate(`/teacher/oireachtas/${eventId}/register`)}
        />}
        {stripePromise && client && (
          <Elements stripe={stripePromise} options={{ clientSecret: client, appearance }}>
            <PayBody eventId={eventId} quote={quote} amountCents={amountCents} kind={kind}
              totalCents={totalCents} balanceAfterCents={balanceAfterCents} balanceDueAt={balanceDueAt} />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default PaymentOireachtas;
